import { z } from "zod";

// JSON Lines sent by `pnpm polaris:wa-sync` to the server import command: one
// header, then media files, then chunks of Meta-shaped history threads, then the
// WhatsApp profile photos of the chats and the chats read on WhatsApp.
export const LOCAL_IMPORT_BUNDLE_VERSION = 1;
export const LOCAL_IMPORT_AVATAR_MAX_BYTES = 512 * 1024;
/** About an hour of speech: long audio files sent instead of voice notes fit whole. */
export const LOCAL_IMPORT_TRANSCRIPT_MAX_CHARS = 60_000;

const whatsappDigitsSchema = z.string().regex(/^[1-9]\d{6,14}$/);

export const localImportHeaderSchema = z
  .object({
    type: z.literal("header"),
    version: z.literal(LOCAL_IMPORT_BUNDLE_VERSION),
    workspaceSlug: z.string().min(1).max(64),
    phoneNumber: z.string().min(7).max(32),
    generatedAt: z.string().datetime({ offset: true }),
    origin: z.enum(["whatsapp_desktop_mac", "iphone_backup"]),
    /** Supplements never contain chat history, attachments or read receipts. */
    mode: z.literal("supplement").optional(),
  })
  .strict();

export const localImportTranscriptSchema = z
  .object({
    text: z.string().min(1).max(LOCAL_IMPORT_TRANSCRIPT_MAX_CHARS),
    model: z.string().min(1).max(120),
    language: z.string().min(2).max(16).nullable(),
  })
  .strict();

export const localImportMediaSchema = z
  .object({
    type: z.literal("media"),
    /** Provider message id of the message the file belongs to. */
    messageId: z.string().min(1).max(200),
    mediaId: z.string().min(1).max(200),
    mimeType: z.string().min(1).max(200),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    size: z.number().int().positive(),
    /** File bytes, base64. */
    data: z.string().min(1),
    transcript: localImportTranscriptSchema.nullable(),
  })
  .strict();

export const localImportChunkSchema = z
  .object({
    type: z.literal("chunk"),
    order: z.number().int().nonnegative(),
    contacts: z.array(
      z
        .object({
          wa_id: whatsappDigitsSchema,
          profile: z.object({ name: z.string().min(1).max(200) }).strict(),
        })
        .strict(),
    ),
    threads: z
      .array(
        z
          .object({
            id: whatsappDigitsSchema,
            // Validated as WhatsApp Cloud API messages by the history processor.
            messages: z.array(z.record(z.unknown())).min(1).max(1_000),
          })
          .strict(),
      )
      .min(1),
  })
  .strict();

export const localImportAvatarSchema = z
  .object({
    type: z.literal("avatar"),
    /** Number of the chat the photo belongs to. */
    waId: whatsappDigitsSchema,
    mimeType: z.literal("image/jpeg"),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    size: z.number().int().positive().max(LOCAL_IMPORT_AVATAR_MAX_BYTES),
    /** File bytes, base64. */
    data: z.string().min(1),
  })
  .strict();

export const localImportReadSchema = z
  .object({
    type: z.literal("read"),
    waId: whatsappDigitsSchema,
    /** Unix seconds: every message of the chat up to here was read on WhatsApp. */
    readUpTo: z.number().int().positive(),
  })
  .strict();

export const localImportCallSchema = z
  .object({
    type: z.literal("call"),
    waId: whatsappDigitsSchema,
    callId: z.string().min(1).max(200),
    desktopPk: z.number().int().positive(),
    startedAt: z.string().datetime({ offset: true }),
    direction: z.enum(["inbound", "outbound"]),
    video: z.boolean(),
    missed: z.boolean(),
    durationSeconds: z.number().finite().nonnegative().nullable(),
    /** Undocumented WhatsApp codes: retain them without guessing their meaning. */
    outcomeCode: z.number().int().nullable(),
    missedReasonCode: z.number().int().nullable(),
    participantOutcomeCode: z.number().int().nullable(),
    bytesReceived: z.number().int().nonnegative().nullable(),
    bytesSent: z.number().int().nonnegative().nullable(),
    lightweight: z.boolean().nullable(),
    scheduledId: z.string().max(200).nullable(),
    reminderId: z.string().max(200).nullable(),
  })
  .strict();
export type LocalImportCall = z.infer<typeof localImportCallSchema>;

/** A file the Mac does not send because it is bigger than the import limit. */
export const localImportMediaSkippedSchema = z
  .object({
    type: z.literal("media_skipped"),
    messageId: z.string().min(1).max(200),
    mediaId: z.string().min(1).max(200),
    reason: z.literal("too_large"),
    size: z.number().int().positive(),
  })
  .strict();

export const localImportLineSchema = z.discriminatedUnion("type", [
  localImportHeaderSchema,
  localImportMediaSchema,
  localImportChunkSchema,
  localImportAvatarSchema,
  localImportReadSchema,
  localImportMediaSkippedSchema,
  localImportCallSchema,
]);
export type LocalImportLine = z.infer<typeof localImportLineSchema>;
export type LocalImportChunk = z.infer<typeof localImportChunkSchema>;
export type LocalImportMedia = z.infer<typeof localImportMediaSchema>;
export type LocalImportAvatar = z.infer<typeof localImportAvatarSchema>;
export type LocalImportRead = z.infer<typeof localImportReadSchema>;
export type LocalImportMediaSkipped = z.infer<typeof localImportMediaSkippedSchema>;

export const localImportResultSchema = z.discriminatedUnion("ok", [
  z
    .object({
      ok: z.literal(true),
      connectionId: z.string(),
      firstImport: z.boolean(),
      messages: z.object({ processed: z.number().int(), skipped: z.number().int() }).strict(),
      media: z
        .object({ stored: z.number().int(), updated: z.number().int(), rejected: z.number().int() })
        .strict(),
      // `avatars`, `reads` and `skippedMedia` are present only when the bundle carried such lines,
      // so older wa-sync clients, which parse this strictly, keep working.
      avatars: z
        .object({
          stored: z.number().int(),
          unchanged: z.number().int(),
          unmatched: z.number().int(),
          rejected: z.number().int(),
        })
        .strict()
        .optional(),
      reads: z
        .object({
          marked: z.number().int(),
          unchanged: z.number().int(),
          unmatched: z.number().int(),
        })
        .strict()
        .optional(),
      skippedMedia: z
        .object({ marked: z.number().int(), unmatched: z.number().int() })
        .strict()
        .optional(),
      importedAt: z.string().datetime({ offset: true }),
      calls: z
        .object({
          stored: z.number().int(),
          updated: z.number().int(),
          unchanged: z.number().int(),
          unmatched: z.number().int(),
        })
        .strict()
        .optional(),
      /** Unmatched records must be retried when their lead/conversation becomes available. */
      retryAvatars: z.array(whatsappDigitsSchema).optional(),
      retryCalls: z.array(z.string()).optional(),
    })
    .strict(),
  z.object({ ok: z.literal(false), code: z.string(), message: z.string() }).strict(),
]);
export type LocalImportResult = z.infer<typeof localImportResultSchema>;
